import React, { useEffect, useState } from 'react';
import './SettingsModal.css';
import ThemeToggle from '../components/ThemeToggle'; // Add this import
import supabase from '../supabaseClient';
import Toast from '../pages/Academy/Academylms/common/Toast';

const SettingsModal = ({ isOpen, onClose, currentUser }) => {
  const [activeTab, setActiveTab] = useState('account');
  const [formData, setFormData] = useState({
    username: currentUser?.username || '',
    email: currentUser?.email || '',
    language: currentUser?.settings?.language || 'en',
    theme: currentUser?.settings?.theme || 'dark',
    notifications: currentUser?.settings?.notifications || {
      messages: true,
      reminders: true,
      updates: false
    },
    wall_privacy: 'public'
  });

  const [loadedPrivacy, setLoadedPrivacy] = useState('public');
  const [loadedMatching, setLoadedMatching] = useState({
    allow_demographic_matching: false,
    show_gender_on_profile: false,
    show_age_on_profile: false,
    match_preferences: { preferred_age_range: { min: null, max: null }, preferred_genders: [] },
  });
  const [matchingForm, setMatchingForm] = useState({
    allow_demographic_matching: false,
    show_gender_on_profile: false,
    show_age_on_profile: false,
    preferred_age_min: '',
    preferred_age_max: '',
    preferred_genders: [],
  });

  // Local toast
  const [toast, setToast] = useState({ open: false, message: '', type: 'success', duration: 2500 });
  const showToast = (message, type = 'success', duration = 2500) => {
    setToast({ open: true, message, type, duration });
  };
  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      notifications: {
        ...formData.notifications,
        [name]: checked
      }
    });
  };

  // Load profile privacy settings
  useEffect(() => {
    async function loadPrivacyAndMatching() {
      try {
        if (!currentUser?.id) return;
        const { data } = await supabase
          .from('profiles')
          .select('wall_privacy, allow_demographic_matching, show_gender_on_profile, show_age_on_profile, match_preferences')
          .eq('id', currentUser.id)
          .maybeSingle();
        const wp = (data && data.wall_privacy) ? data.wall_privacy : 'public';
        setLoadedPrivacy(wp);
        setFormData(prev => ({ ...prev, wall_privacy: wp }));

        const mp = (data && data.match_preferences && typeof data.match_preferences === 'object') ? data.match_preferences : {};
        const preferredAge = (mp && mp.preferred_age_range && typeof mp.preferred_age_range === 'object') ? mp.preferred_age_range : {};
        const preferredGenders = Array.isArray(mp?.preferred_genders) ? mp.preferred_genders : [];

        const loaded = {
          allow_demographic_matching: !!data?.allow_demographic_matching,
          show_gender_on_profile: !!data?.show_gender_on_profile,
          show_age_on_profile: !!data?.show_age_on_profile,
          match_preferences: {
            preferred_age_range: {
              min: typeof preferredAge?.min === 'number' ? preferredAge.min : null,
              max: typeof preferredAge?.max === 'number' ? preferredAge.max : null,
            },
            preferred_genders: preferredGenders,
          },
        };
        setLoadedMatching(loaded);
        setMatchingForm({
          allow_demographic_matching: loaded.allow_demographic_matching,
          show_gender_on_profile: loaded.show_gender_on_profile,
          show_age_on_profile: loaded.show_age_on_profile,
          preferred_age_min: loaded.match_preferences.preferred_age_range.min == null ? '' : String(loaded.match_preferences.preferred_age_range.min),
          preferred_age_max: loaded.match_preferences.preferred_age_range.max == null ? '' : String(loaded.match_preferences.preferred_age_range.max),
          preferred_genders: loaded.match_preferences.preferred_genders,
        });
      } catch {}
    }
    loadPrivacyAndMatching();
  }, [currentUser?.id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the updated settings to your backend
    console.log('Updated settings:', formData);
    onClose();
  };

  const handleSavePrivacy = async (e) => {
    e.preventDefault();
    if (!currentUser?.id) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ wall_privacy: formData.wall_privacy })
        .eq('id', currentUser.id);
      if (error) throw error;
      showToast('Privacy saved', 'success');
    } catch (err) {
      console.error('Failed to save privacy settings', err);
      showToast('Failed to save privacy', 'error');
    }
  };

  const handleTogglePreferredGender = (gender) => {
    setMatchingForm((prev) => {
      const set = new Set(prev.preferred_genders || []);
      if (set.has(gender)) set.delete(gender);
      else set.add(gender);
      return { ...prev, preferred_genders: Array.from(set) };
    });
  };

  const handleSaveMatching = async (e) => {
    e.preventDefault();
    if (!currentUser?.id) return;

    const minIn = matchingForm.preferred_age_min === '' ? null : parseInt(matchingForm.preferred_age_min, 10);
    const maxIn = matchingForm.preferred_age_max === '' ? null : parseInt(matchingForm.preferred_age_max, 10);
    const min = Number.isFinite(minIn) ? minIn : null;
    const max = Number.isFinite(maxIn) ? maxIn : null;
    if (min != null && (min < 13 || min > 120)) {
      showToast('Preferred minimum age must be between 13 and 120', 'error');
      return;
    }
    if (max != null && (max < 13 || max > 120)) {
      showToast('Preferred maximum age must be between 13 and 120', 'error');
      return;
    }
    if (min != null && max != null && min > max) {
      showToast('Preferred minimum age cannot exceed maximum age', 'error');
      return;
    }

    const patch = {
      allow_demographic_matching: !!matchingForm.allow_demographic_matching,
      show_gender_on_profile: !!matchingForm.show_gender_on_profile,
      show_age_on_profile: !!matchingForm.show_age_on_profile,
      match_preferences: {
        preferred_age_range: { min, max },
        preferred_genders: Array.isArray(matchingForm.preferred_genders) ? matchingForm.preferred_genders : [],
      },
    };

    try {
      const { error } = await supabase
        .from('profiles')
        .update(patch)
        .eq('id', currentUser.id);
      if (error) throw error;
      setLoadedMatching({
        allow_demographic_matching: patch.allow_demographic_matching,
        show_gender_on_profile: patch.show_gender_on_profile,
        show_age_on_profile: patch.show_age_on_profile,
        match_preferences: patch.match_preferences,
      });
      showToast('Match preferences saved', 'success');
    } catch (err) {
      console.error('Failed to save match preferences', err);
      showToast('Failed to save match preferences', 'error');
    }
  };

  // Only skip rendering; keep hooks order consistent
  if (!isOpen) return null;

  const isPrivacyDirty = formData.wall_privacy !== loadedPrivacy;
  const isMatchingDirty =
    matchingForm.allow_demographic_matching !== loadedMatching.allow_demographic_matching ||
    matchingForm.show_gender_on_profile !== loadedMatching.show_gender_on_profile ||
    matchingForm.show_age_on_profile !== loadedMatching.show_age_on_profile ||
    (matchingForm.preferred_age_min === '' ? null : parseInt(matchingForm.preferred_age_min, 10)) !== loadedMatching.match_preferences.preferred_age_range.min ||
    (matchingForm.preferred_age_max === '' ? null : parseInt(matchingForm.preferred_age_max, 10)) !== loadedMatching.match_preferences.preferred_age_range.max ||
    JSON.stringify((matchingForm.preferred_genders || []).slice().sort()) !== JSON.stringify((loadedMatching.match_preferences.preferred_genders || []).slice().sort());

  return (
    <div className="settings-modal-overlay" onMouseDown={onClose}>
      <div className="settings-modal" onMouseDown={(e) => e.stopPropagation()}>
        <Toast
          open={toast.open}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={closeToast}
        />
        <div className="settings-header">
          <h2>Settings</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="settings-content">
          <div className="settings-tabs">
            <button 
              className={`tab-button ${activeTab === 'account' ? 'active' : ''}`}
              onClick={() => setActiveTab('account')}
            >
              Account
            </button>
            <button 
              className={`tab-button ${activeTab === 'preferences' ? 'active' : ''}`}
              onClick={() => setActiveTab('preferences')}
            >
              Preferences
            </button>
            <button 
              className={`tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
              onClick={() => setActiveTab('notifications')}
            >
              Notifications
            </button>
            <button 
              className={`tab-button ${activeTab === 'privacy' ? 'active' : ''}`}
              onClick={() => setActiveTab('privacy')}
            >
              Privacy
            </button>
            <button 
              className={`tab-button ${activeTab === 'matching' ? 'active' : ''}`}
              onClick={() => setActiveTab('matching')}
            >
              Matching
            </button>
          </div>
          <div className="settings-panel">
            {activeTab === 'preferences' && (
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Language</label>
                  <select 
                    name="language" 
                    value={formData.language} 
                    onChange={handleInputChange}
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="ja">Japanese</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Theme</label>
                  <ThemeToggle /> {/* Add the toggle here */}
                </div>
                <div className="form-actions">
                  <button type="submit" className="save-button">Save Changes</button>
                </div>
              </form>
            )}
            {activeTab === 'account' && (
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Username</label>
                  <input 
                    type="text" 
                    name="username" 
                    value={formData.username} 
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="save-button">Save Changes</button>
                </div>
              </form>
            )}
            {activeTab === 'notifications' && (
              <form onSubmit={handleSubmit}>
                <div className="checkbox-group">
                  <label>
                    <input 
                      type="checkbox" 
                      name="messages" 
                      checked={formData.notifications.messages} 
                      onChange={handleCheckboxChange}
                    />
                    Message notifications
                  </label>
                </div>
                <div className="checkbox-group">
                  <label>
                    <input 
                      type="checkbox" 
                      name="reminders" 
                      checked={formData.notifications.reminders} 
                      onChange={handleCheckboxChange}
                    />
                    Learning reminders
                  </label>
                </div>
                <div className="checkbox-group">
                  <label>
                    <input 
                      type="checkbox" 
                      name="updates" 
                      checked={formData.notifications.updates} 
                      onChange={handleCheckboxChange}
                    />
                    Platform updates
                  </label>
                </div>
                <div className="form-actions">
                  <button type="submit" className="save-button">Save Changes</button>
                </div>
              </form>
            )}
            {activeTab === 'privacy' && (
              <form onSubmit={handleSavePrivacy}>
                <div className="form-group">
                  <label>Wall visibility</label>
                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        name="wall_privacy"
                        value="public"
                        checked={formData.wall_privacy === 'public'}
                        onChange={(e) => setFormData(prev => ({ ...prev, wall_privacy: e.target.value }))}
                      />
                      Public (any logged-in user can view and post as allowed)
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="wall_privacy"
                        value="connections"
                        checked={formData.wall_privacy === 'connections'}
                        onChange={(e) => setFormData(prev => ({ ...prev, wall_privacy: e.target.value }))}
                      />
                      Connections only
                    </label>
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="save-button" disabled={!isPrivacyDirty}>Save Privacy</button>
                </div>
              </form>
            )}

            {activeTab === 'matching' && (
              <form onSubmit={handleSaveMatching}>
                <div className="form-group">
                  <label>Allow demographic matching</label>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={matchingForm.allow_demographic_matching}
                        onChange={(e) => setMatchingForm((prev) => ({ ...prev, allow_demographic_matching: e.target.checked }))}
                      />
                      Let others filter/match by my age & gender (not displayed unless I choose)
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Show on my profile</label>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={matchingForm.show_gender_on_profile}
                        onChange={(e) => setMatchingForm((prev) => ({ ...prev, show_gender_on_profile: e.target.checked }))}
                      />
                      Show my gender
                    </label>
                  </div>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={matchingForm.show_age_on_profile}
                        onChange={(e) => setMatchingForm((prev) => ({ ...prev, show_age_on_profile: e.target.checked }))}
                      />
                      Show my age
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Preferred partner age range</label>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="13"
                      max="120"
                      placeholder="Min"
                      value={matchingForm.preferred_age_min}
                      onChange={(e) => setMatchingForm((prev) => ({ ...prev, preferred_age_min: e.target.value }))}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, marginTop: 10 }}>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="13"
                      max="120"
                      placeholder="Max"
                      value={matchingForm.preferred_age_max}
                      onChange={(e) => setMatchingForm((prev) => ({ ...prev, preferred_age_max: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Preferred partner gender</label>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={(matchingForm.preferred_genders || []).includes('male')}
                        onChange={() => handleTogglePreferredGender('male')}
                      />
                      Male
                    </label>
                  </div>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={(matchingForm.preferred_genders || []).includes('female')}
                        onChange={() => handleTogglePreferredGender('female')}
                      />
                      Female
                    </label>
                  </div>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={(matchingForm.preferred_genders || []).includes('nonbinary')}
                        onChange={() => handleTogglePreferredGender('nonbinary')}
                      />
                      Non-binary
                    </label>
                  </div>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={(matchingForm.preferred_genders || []).includes('other')}
                        onChange={() => handleTogglePreferredGender('other')}
                      />
                      Other
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="save-button" disabled={!isMatchingDirty}>Save Match Preferences</button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
